import React, { ChangeEvent, FC, ReactNode, useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    TextField,
    MenuItem,
    Checkbox,
    Button,
    Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlatformLogo from '../../components/Logo/PlatformLogo';
import { DspEntity } from '../../types/DspEntity';
import { SasAccount } from '../../types/SasAccount';

interface DspIntegrationMapProps {
    dspEntities: DspEntity[];
    dv360Entities: DspEntity[];
    sasAccounts: SasAccount[];
    dspEntityIds: number[];
    setDspEntityIds: (dspEntityIds: number[]) => void;
    dv360EntityIds: number[]; // Changed from dv360Entity: DspEntity | null
    setDv360EntityIds: (dv360EntityIds: number[]) => void; // Changed from setDv360Entity
    sasAccountIds: number[];
    setSasAccountIds: (sasAccountIds: number[]) => void;
}

const AgencyDspIntegrationMap: FC<DspIntegrationMapProps> = ({
    dspEntities,
    dv360Entities,
    dspEntityIds,
    setDspEntityIds,
    dv360EntityIds,
    setDv360EntityIds,
    sasAccountIds,
    setSasAccountIds,
    sasAccounts,
}) => {
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
    const [isOverriding, setIsOverriding] = useState<boolean>(false);

    function renderSelectedDspEntities(value: unknown): ReactNode {
        let selected: string[] = [];
        let values = value as number[];
        values.forEach((id) => {
            let dspEntity = dspEntities.find((dspEntity) => dspEntity.id === id);
            if (dspEntity) {
                selected.push(dspEntity.entityName);
            }
        });
        return (
            <Typography variant="body2" component="span">
                {selected.join(', ')}
            </Typography>
        );
    }

    function renderSelectedDV360Entity(value: unknown): ReactNode {
        if (value === null || value === undefined || value === '') {
            return <Typography variant="body2" component="span"></Typography>;
        }
        const id = value as number;
        const dv360Entity = dv360Entities.find((entity) => entity.id === id);
        return (
            <Typography variant="body2" component="span">
                {dv360Entity ? dv360Entity.entityName : ''}
            </Typography>
        );
    }

    function handleDspEntityChange(event: ChangeEvent<HTMLInputElement>) {
        if (Array.isArray(event.target.value)) {
            setDspEntityIds(event.target.value);
        }
    }

    function handleDV360EntityChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        if (value === '' || value === null) {
            // Clear selection - set to empty array
            setDv360EntityIds([]);
        } else {
            // Set selection - array with single ID
            const id = Number(value);
            setDv360EntityIds([id]);
        }
    }

    function handleSasAccountChange(event: ChangeEvent<HTMLInputElement>) {
        if (Array.isArray(event.target.value)) {
            setSasAccountIds(event.target.value);
        }
    }

    function renderSelectedSasAccounts(value: unknown): ReactNode {
        let selected: string[] = [];
        let values = value as number[];
        values.forEach((id) => {
            let sasAccount = sasAccounts.find((sasAccount) => sasAccount.id === id);
            if (sasAccount) {
                selected.push(sasAccount.accountName);
            }
        });
        return (
            <Typography variant="body2" component="span">
                {selected.join(', ')}
            </Typography>
        );
    }

    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const handleOverrideClick = () => {
        setIsOverriding(true);
    };

    const handleRemoveSasAccount = (idToRemove: number) => {
        setSasAccountIds(sasAccountIds.filter((id) => id !== idToRemove));
    };

    // Get the currently selected DV360 entity ID (first element of array, or empty string)
    const selectedDv360EntityId = dv360EntityIds.length > 0 ? dv360EntityIds[0] : '';

    return (
        <Box sx={{ width: '100%' }}>
            <Accordion
                expanded={expandedPanel === 'amazon'}
                onChange={handleAccordionChange('amazon')}
                sx={{
                    mb: 2,
                    '&:before': {
                        display: 'none',
                    },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PlatformLogo size="Small" platform="AMAZON" />
                        <Typography variant="h6" fontWeight={500}>
                            Amazon DSP
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        backgroundColor: '#fafafa',
                        borderTop: '1px solid #e0e0e0',
                        p: 3,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure Amazon DSP settings for this Agency.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Available DSP Entities"
                        name="dspEntities"
                        value={dspEntityIds}
                        variant="outlined"
                        required={true}
                        size="small"
                        select
                        onChange={handleDspEntityChange}
                        sx={{
                            mb: 3,
                            maxWidth: 470,
                            '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                            },
                            '& .MuiSelect-select': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                            },
                        }}
                        slotProps={{
                            select: {
                                multiple: true,
                                renderValue: renderSelectedDspEntities,
                            },
                            inputLabel: { shrink: true },
                        }}
                    >
                        {dspEntities.map((dspEntity) => {
                            return (
                                <MenuItem key={dspEntity.id} value={dspEntity.id} sx={{ py: 0.75 }}>
                                    <Checkbox
                                        checked={dspEntityIds.find((d) => d === dspEntity.id) !== undefined}
                                        size="small"
                                        sx={{ py: 0 }}
                                    />
                                    <Typography variant="body2">{dspEntity.entityName}</Typography>
                                </MenuItem>
                            );
                        })}
                    </TextField>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Ad Server Account
                        </Typography>

                        {!isOverriding ? (
                            <Box
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    p: 2,
                                    backgroundColor: '#fff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '42%',
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                                        Default from Settings tab
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {sasAccountIds.map((id) => {
                                            const sasAccount = sasAccounts.find((acc) => acc.id === id);
                                            return sasAccount ? (
                                                <Chip
                                                    key={id}
                                                    label={sasAccount.accountName}
                                                    onDelete={() => handleRemoveSasAccount(id)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        '& .MuiChip-label': {
                                                            fontSize: '0.813rem',
                                                        },
                                                    }}
                                                />
                                            ) : null;
                                        })}
                                    </Box>
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOverrideClick}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                        minWidth: 80,
                                    }}
                                >
                                    Override
                                </Button>
                            </Box>
                        ) : (
                            <TextField
                                fullWidth
                                label="Available AAS Accounts"
                                name="sasAccounts"
                                value={sasAccountIds}
                                variant="outlined"
                                required={true}
                                size="small"
                                select
                                onChange={handleSasAccountChange}
                                sx={{
                                    maxWidth: 470,
                                    '& .MuiInputBase-root': {
                                        fontSize: '0.875rem',
                                    },
                                    '& .MuiSelect-select': {
                                        paddingTop: '8px',
                                        paddingBottom: '8px',
                                    },
                                }}
                                slotProps={{
                                    select: {
                                        multiple: true,
                                        renderValue: renderSelectedSasAccounts,
                                    },
                                    inputLabel: { shrink: true },
                                }}
                            >
                                {sasAccounts.map((sasAccount) => {
                                    return (
                                        <MenuItem key={sasAccount.id} value={sasAccount.id} sx={{ py: 0.75 }}>
                                            <Checkbox
                                                checked={sasAccountIds.find((d) => d === sasAccount.id) !== undefined}
                                                size="small"
                                                sx={{ py: 0 }}
                                            />
                                            <Typography variant="body2">{sasAccount.accountName}</Typography>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Google DV360 Section */}
            <Accordion
                expanded={expandedPanel === 'google'}
                onChange={handleAccordionChange('google')}
                sx={{
                    '&:before': {
                        display: 'none',
                    },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PlatformLogo size="Small" platform="DV360" />
                        <Typography variant="h6" fontWeight={500}>
                            Google DV360
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        backgroundColor: '#fafafa',
                        borderTop: '1px solid #e0e0e0',
                        p: 3,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                        },
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure Google DV360 settings for this Agency.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Available DV360 Entities"
                        name="dv360Entities"
                        value={selectedDv360EntityId}
                        variant="outlined"
                        required={false}
                        size="small"
                        select
                        onChange={handleDV360EntityChange}
                        sx={{
                            mb: 3,
                            maxWidth: 470,
                            '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                            },
                            '& .MuiSelect-select': {
                                paddingTop: '8px',
                                paddingBottom: '8px',
                            },
                        }}
                        slotProps={{
                            select: {
                                multiple: false,
                                renderValue: renderSelectedDV360Entity,
                            },
                            inputLabel: { shrink: true },
                        }}
                    >
                        <MenuItem value="">
                            <Typography variant="body2" color="text.secondary">
                                None
                            </Typography>
                        </MenuItem>
                        {dv360Entities.map((dv360Entity) => {
                            return (
                                <MenuItem key={dv360Entity.id} value={dv360Entity.id} sx={{ py: 0.75 }}>
                                    <Typography variant="body2">{dv360Entity.entityName}</Typography>
                                </MenuItem>
                            );
                        })}
                    </TextField>

                    <Box sx={{ mb: 2 }}></Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default AgencyDspIntegrationMap;
